import { useAuth, useCachedFetch } from '@structural-climbing/auth'
import type { SubscriptionStatus } from '@structural-climbing/ui'

/** What Home needs about a child: who, whether their plan lets them in. */
export interface ChildSummary {
  id: string
  first_name: string
  last_name: string | null
  active_subscription: {
    effective_status: SubscriptionStatus
    entries_remaining: number | null
    gym_plans: { name: string }
  } | null
}

/** Shared with FamilySection, which clears it after every change it makes. */
export const MY_CHILDREN_CACHE_PREFIX = 'my-children:'

/**
 * The member's children, each with their plan at this gym.
 *
 * The same `/my-children` request the Family card made on Home before it moved
 * to Contul meu, so Home makes no more calls than it did.
 */
export function useMyChildren(gymId: string | null) {
  const { isAuthenticated, getAccessTokenSilently } = useAuth()
  const apiUrl = import.meta.env.VITE_API_URL as string

  const { data } = useCachedFetch<ChildSummary[]>(
    isAuthenticated && gymId ? `${MY_CHILDREN_CACHE_PREFIX}${gymId}` : null,
    async () => {
      const token = await getAccessTokenSilently()
      const res = await fetch(`${apiUrl}/my-children?gym_id=${encodeURIComponent(gymId!)}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!res.ok) throw new Error('Failed to load children')
      return ((await res.json()) as { children: ChildSummary[] }).children
    },
  )

  return data ?? []
}
